import MassRange from './MassRange.js';
import Wavelength from './Wavelength.js';
import {floatPointsFromBase64String} from '../util/CommonUtils.js';
import IntegrationScope from "./chromatogram/integration/IntegrationScope.js";
import Range from "./Range.js";
import EicExtractionProps from "../../components/massSpec/EicExtractionProps.js";
import DetectedPeakJson from "./DetectedPeakJson.js";

export default class ChromatogramJson{
  constructor(id, domainId, signalId, detectorRunId, isTotalSignal, substanceId, massRange, wl, wlColor,
              baselineAnchors, signalRange, name, eicExtractionProps, substance, detectorRunProps, detectedPeaks) {
    /** @type {string} */
    this._chromatogramId = id;
    /** @type {string} */
    this._domainBlobId = domainId;
    /** @type {string} */
    this._signaBloblId = signalId;
    /** @type {string} */
    this._detectorRunId = detectorRunId;
    /** @type {boolean} */
    this._isTotalSignal = isTotalSignal;
    /** @type {string} */
    this._substanceId = substanceId;
    /** @type {MassRange} */
    this._massRange = massRange;
    /** @type {Wavelength} */
    this._wavelength = wl;
    /** @type {string} */
    this._wavelengthColor = wlColor;
    /** @type {FloatPoint[]} */
    this._baselineAnchors = baselineAnchors;
    this._signalRange = signalRange;
    this._name = name;
    this._eicExtractionProps = eicExtractionProps;
    /** @type {SubstanceJson} */
    this._substance = substance;
    /** @type {DetectorRunProperties} */
    this._detectorRunProperties = detectorRunProps;
    /** @type {DetectedPeakJson[]} */
    this._detectedPeaks = detectedPeaks || [];
  }

  /** @return {string} */
  getId() {return this._chromatogramId}
  /** @return {string} */
  getDomainId() {return this._domainBlobId}
  /** @return {string} */
  getSignalId() {return this._signaBloblId}
  /** @return {FloatPoint[]} */
  getBaselineAnchors() {return this._baselineAnchors}
  /** @param {FloatPoint[]} anchors */
  setBaselineAnchors(anchors) {this._baselineAnchors = anchors}
  /** @return {string} */
  getDetectorRunId() {return this._detectorRunId}
  /** @return {boolean} */
  isTotalSignal() {return this._isTotalSignal}
  /** @return {string} */
  getSubstanceId() {return this._substanceId}
  /** @return {MassRange} */
  getMassRange() {return this._massRange}
  /** @return {Wavelength} */
  getWl() {return this._wavelength}
  /** @return {string} */
  getWlColor() {return this._wavelengthColor}
  /** @return {Range} */
  getSignalRange() {return this._signalRange}
  /** @return {DetectedPeakJson[]} */
  getDetectedPeaks() {return this._detectedPeaks}

  getName(){
    if (!this.isTotalSignal() && this._substance && this._detectorRunProperties.isProductIonScan())
      return  this._name + " " + this._substance.getDisplayName()
    return this._name
  }

  /**
   * @return {AnalyticalMethod}
   */
  getAnalyticalMethod() {
    return this._detectorRunProperties.getAnalyticalMethod();
  }

  /** @returns {IntegrationScope} */
  getScope(){
    return new IntegrationScope(this.getName())
  }
  /**
   * @returns {EicExtractionProps}
   */
  getEicExtractionProps(){
    return this._eicExtractionProps
  }

  /** @param {SubstanceJson} substance */
  setSubstance(substance) {
    if (this._substanceId != null && this._substanceId !== substance.getId())
      throw new Error("Can't set substance, substance id is different");
    this._substance = substance;
  }

  /** @returns {SubstanceJson} */
  getSubstance(){
    return this._substance
  }

  /** @returns {DetectorRunProperties} */
  getDetectorRunProperties(){
    return this._detectorRunProperties
  }

  /** @param {DetectorRunProperties} detectorRunProperties */
  setDetectorRunProperties(detectorRunProperties) {
    if (this._detectorRunId !== detectorRunProperties.getId())
      throw new Error("Can't set detectorRunProperties, detectorRun id is different");
    this._detectorRunProperties = detectorRunProperties;
  }
  /**
   * @param json
   * @returns {ChromatogramJson}
   */
  static parseServerJson(json){
    const massRange = MassRange.parseServerJson(json.massRange);
    const wl = Wavelength.parseServerJson(json.wavelength);
    const baselineAnchors = floatPointsFromBase64String(json.base64EncodedAnchors);
    const signalRange = new Range(json.minSignalIntensity, json.maxSignalIntensity);
    const detectedPeaks = DetectedPeakJson.parseManyFromServerBytes(json.base64EncodedDetectedPeaks);
    const eicExtractionProps = json.eicExtractionProps ?
        EicExtractionProps.parseServerJson(json.eicExtractionProps) : null;
    return new ChromatogramJson(json.chromatogramId, json.domainId, json.signalId, json.detectorId,
      json.totalSignal, json.substanceId, massRange, wl, json.wavelengthColor, baselineAnchors, signalRange, json.name,
        eicExtractionProps, null, null, detectedPeaks);
  }
}
